/**
 * Hook para verificar disponibilidad de CUIT en tiempo real.
 * Usa TanStack Query con staleTime de 10s para reducir llamadas excesivas.
 * Solo realiza la verificación cuando el CUIT tiene exactamente 11 dígitos.
 *
 * @example
 * const { data, isLoading } = useVerificarCUIT(cuit, clienteId)
 * if (!data?.disponible) {
 *   console.log('CUIT ya registrado para:', data.clienteExistente?.nombre)
 * }
 */

import { useQuery } from '@tanstack/react-query'
import { useSupabase } from '@/lib/supabase-context'
import { useOrganizacion } from '@/core/providers/OrganizacionProvider'
import { personasService, type VerificacionCUIT } from '@/services/personasService'

// Query key factory
export const verificarCUITKeys = {
  all: ['verificar-cuit'] as const,
  verificacion: (organizacionId: string, cuit: string, excluirClienteId?: string) =>
    [...verificarCUITKeys.all, organizacionId, cuit, excluirClienteId] as const,
}

/**
 * Hook para verificar si un CUIT está disponible en la organización actual.
 *
 * @param cuit - CUIT a verificar (con o sin guiones)
 * @param excluirClienteId - ID del cliente a excluir (para modo edición)
 * @returns Query con resultado de verificación
 */
export function useVerificarCUIT(cuit: string | null | undefined, excluirClienteId?: string) {
  const { supabase, isLoading: supabaseLoading } = useSupabase()
  const { organizacionId } = useOrganizacion()

  // Limpiar CUIT
  const cuitLimpio = cuit?.replace(/\D/g, '') || ''

  // Solo verificar si tiene 11 dígitos
  const debeVerificar = cuitLimpio.length === 11

  return useQuery<VerificacionCUIT>({
    queryKey: verificarCUITKeys.verificacion(organizacionId || '', cuitLimpio, excluirClienteId),
    queryFn: async () => {
      if (!supabase || !organizacionId || !debeVerificar) {
        return { disponible: true }
      }
      return personasService.verificarCUITDisponible(
        supabase,
        cuitLimpio,
        organizacionId,
        excluirClienteId
      )
    },
    enabled: !!supabase && !supabaseLoading && !!organizacionId && debeVerificar,
    staleTime: 10000, // 10 segundos - evita re-fetch frecuentes
    gcTime: 30000, // 30 segundos en cache
  })
}
